export const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'el', label: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'sr', label: 'Srpski (ћирилица)', flag: '🇷🇸' },
  { code: 'ro', label: 'Română', flag: '🇷🇴' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'bg', label: 'Български', flag: '🇧🇬' },
  { code: 'mk', label: 'Македонски', flag: '🇲🇰' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'uk', label: 'Українська', flag: '🇺🇦' },
];

export const supportedLocales = LANGUAGES.map((language) => language.code);
export const DEFAULT_LOCALE = 'en';
export const LOCALE_STORAGE_KEY = 'app_locale';
const LOCALE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

const readCookie = (name) => {
  if (typeof document === 'undefined') {
    return null;
  }
  const prefix = `${name}=`;
  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
};

const writeCookie = (name, value, maxAgeSeconds) => {
  if (typeof document === 'undefined') {
    return;
  }
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; path=/; max-age=${maxAgeSeconds}; samesite=lax`;
};

export const normalizeLocale = (value) => {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const lowered = value.toLowerCase();
  if (supportedLocales.includes(lowered)) {
    return lowered;
  }
  const base = lowered.split('-')[0].split('_')[0];
  return supportedLocales.includes(base) ? base : null;
};

export const getStoredLocale = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const stored = normalizeLocale(
      window.localStorage.getItem(LOCALE_STORAGE_KEY),
    );
    if (stored) {
      return stored;
    }
  } catch (error) {
    // ignore storage errors (private mode, blocked storage, etc.)
  }

  return normalizeLocale(readCookie(LOCALE_STORAGE_KEY));
};

export const setStoredLocale = (locale) => {
  if (typeof window === 'undefined') {
    return;
  }
  const normalized = normalizeLocale(locale);
  if (!normalized) {
    return;
  }
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
  } catch (error) {
    // ignore storage errors (private mode, blocked storage, etc.)
  }
  writeCookie(LOCALE_STORAGE_KEY, normalized, LOCALE_COOKIE_MAX_AGE_SECONDS);
};

export const detectBrowserLocale = () => {
  if (typeof navigator === 'undefined') {
    return null;
  }
  const candidates =
    Array.isArray(navigator.languages) && navigator.languages.length
      ? navigator.languages
      : [navigator.language];

  const match = candidates
    .map((candidate) => normalizeLocale(candidate))
    .find(Boolean);

  return match || null;
};

export const getLocaleFromPathname = (pathname) => {
  if (!pathname || typeof pathname !== 'string') {
    return null;
  }
  const first = pathname.split('/').filter(Boolean)[0];
  return normalizeLocale(first);
};

export const stripLocaleFromPathname = (pathname) => {
  if (!pathname || typeof pathname !== 'string') {
    return '/';
  }

  const parts = pathname.split('/').filter(Boolean);
  const maybeLocale = normalizeLocale(parts[0]);
  const rest = maybeLocale ? parts.slice(1) : parts;
  const restPath = rest.join('/');
  const hadTrailingSlash =
    pathname === '/' || (pathname.length > 1 && pathname.endsWith('/'));

  if (!restPath) {
    return '/';
  }

  return hadTrailingSlash ? `/${restPath}/` : `/${restPath}`;
};

export const buildPathWithLocale = (pathname, locale) => {
  const normalized = normalizeLocale(locale) || DEFAULT_LOCALE;
  const stripped = stripLocaleFromPathname(pathname);
  return `/${normalized}${stripped}`;
};
